/**
 * The maths behind `EdgeScroller` (#262): a row that scrolls sideways inside its box (the top bar's tabs, a long
 * segmented strip) fades the edge its content runs past, and keeps the current item in view.
 */

/** Which edges have content clipped past them. */
export type EdgeFade = "none" | "start" | "end" | "both";

export interface ScrollMetrics {
  readonly scrollLeft: number;
  readonly scrollWidth: number;
  readonly clientWidth: number;
}

/** Sub-pixel slack: layout widths are fractional, scrollWidth is rounded. */
const SLACK = 1;

export function edgeFade({ scrollLeft, scrollWidth, clientWidth }: ScrollMetrics): EdgeFade {
  const start = scrollLeft > SLACK;
  const end = scrollLeft + clientWidth < scrollWidth - SLACK;
  if (start && end) return "both";
  if (start) return "start";
  if (end) return "end";
  return "none";
}

export interface RevealInput extends ScrollMetrics {
  /** The item's edges, in the scroller's content coordinates (0 = the content's left edge). */
  readonly itemLeft: number;
  readonly itemRight: number;
  /** Keep this much clear of each edge, so the item isn't under the fade. */
  readonly inset: number;
}

/**
 * The scrollLeft that shows the item whole, moving as little as possible: unchanged when it's already clear of both
 * fades, else just far enough. An item wider than the box lines up with its left edge. Clamped to the scroll range.
 */
export function revealScrollLeft({ scrollLeft, scrollWidth, clientWidth, itemLeft, itemRight, inset }: RevealInput): number {
  const max = Math.max(0, scrollWidth - clientWidth);
  const clamp = (x: number) => Math.min(max, Math.max(0, x));
  const lo = itemLeft - inset;
  const hi = itemRight + inset - clientWidth;
  if (hi > lo) return clamp(lo);
  if (scrollLeft > lo) return clamp(lo);
  if (scrollLeft < hi) return clamp(hi);
  return clamp(scrollLeft);
}
